import { useEffect, useState } from "react";
import {
  fetchAllSponsors,
  fetchProgramSponsorsByProgramId,
  deleteProgramSponsor,
  createProgramSponsor,
  fetchSponsor
} from "./sponsorAPI";
import {
  Sponsor,
  ProgramSponsors,
  CreateProgramSponsorsInput,
  User
} from "../../API";
import { QueryStatus } from "../../app/types";
import { Theme } from "@mui/material";
import { theme } from "../../theme";
import { fetchUniqueSponsorsFromProgramMemberships } from "../../app/utils";
import { useNetworksByProgram } from "../Networks/networkUtils";
import { pngs } from "../../app/constants";

// Stripping away nested objects to avoid circular references for react-hook-form
// Also ridding fields not necessary for display in the form.
// Can use programID on edit form, but not new (id does not yet exist)
export type ProgramSponsorsPreview = Omit<
  ProgramSponsors,
  "sponsor" | "program" | "programID" | "id" | "createdAt" | "updatedAt"
>;

export const useAllSponsors = () => {
  const [sponsors, setSponsors] = useState<Sponsor[] | null>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getAllSponsors = async () => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchAllSponsors();
      setSponsors(results);
      setStatus("succeeded");
    } catch (error) {
      console.error("Error loading sponsors:", error);
      setStatus("failed");
      setError(`Error loading sponsors: ${error}`);
    }
  };

  useEffect(() => {
    getAllSponsors();
  }, []);

  return {
    sponsors,
    status,
    error,
    getAllSponsors
  };
};

export const useUpdateProgramSponsors = () => {
  const [updatedProgramSponsors, setUpdatedProgramSponsors] = useState<
    ProgramSponsors[] | null
  >(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateProgramSponsors = async (
    programId: string,
    programSponsors: string[]
  ) => {
    setStatus("loading");
    setError(null);
    try {
      // Get the programs's existing ProgramSponsors
      const existingProgramSponsors = await fetchProgramSponsorsByProgramId(
        programId
      );
      if (existingProgramSponsors) {
        // Delete the existing ProgramSponsors
        for (const existingProgramSponsor of existingProgramSponsors) {
          if (existingProgramSponsor) {
            const deletionResult = await deleteProgramSponsor(
              existingProgramSponsor.id
            );
          }
        }
      }
      if (programSponsors) {
        // Create new ProgramSponsors
        for (const programSponsor of programSponsors) {
          if (programSponsor) {
            // Need to create the question before creating the answerOptions for it
            const input: CreateProgramSponsorsInput = {
              sponsorID: programSponsor,
              programID: programId
            };
            const createProgramSponsorsResult = await createProgramSponsor(
              input
            );
          }
        }
      }
      setStatus("succeeded");
    } catch (error) {
      let errorMessage;
      if (error instanceof Error) errorMessage = error.message;
      const errorString = `Error updating ProgramSponsors: ${errorMessage}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
  };

  return {
    updatedProgramSponsors,
    status,
    error,
    updateProgramSponsors
  };
};

export const useSponsorBranding = () => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(theme);
  const [logoFullMonoURL, setLogoFullMonoURL] = useState<string>(
    pngs.simplLogoFull
  );
  const [logoSquareMonoURL, setLogoSquareMonoURL] = useState<string>(
    pngs.simplLogoSquare
  );
  const [sponsorName, setSponsorName] = useState("SIMPL");
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const { getNetworksByProgram } = useNetworksByProgram();

  const getSponsorBranding = async (user: User) => {
    try {
      setStatus("loading");
      setError(null);

      const usersProgramMemberships = user.programMemberships?.items;
      if (usersProgramMemberships) {
        const usersUniqueSponsors =
          await fetchUniqueSponsorsFromProgramMemberships(
            usersProgramMemberships,
            getNetworksByProgram
          );

        // Use the default/SIMPL branding if the user has either none or more than 1 Sponsor.
        if (usersUniqueSponsors.length === 1) {
          const usersOnlySponsor = usersUniqueSponsors[0];
          // Need to make another query due to the previous queries depth not reaching the branding.
          const sponsor = await fetchSponsor(usersOnlySponsor.id);
          const sponsorBrandingToUse = sponsor?.branding;
          const sponsorPrimaryColor = sponsorBrandingToUse?.colors?.primary;
          const sponsorSecondaryColor = sponsorBrandingToUse?.colors?.secondary;
          const sponsorLightColor = sponsorBrandingToUse?.colors?.light;
          const sponsorLogoFullMonoURL = sponsorBrandingToUse?.logos?.fullMono;
          const sponsorLogoSquareMonoURL =
            sponsorBrandingToUse?.logos?.squareMono;
          const newTheme: Theme = { ...currentTheme };
          if (sponsorPrimaryColor) {
            newTheme.palette.primary.main = sponsorPrimaryColor;
          }
          if (sponsorSecondaryColor) {
            newTheme.palette.secondary.main = sponsorSecondaryColor;
          }
          if (sponsorLightColor && newTheme.palette.light) {
            newTheme.palette.light.main = sponsorLightColor;
          }
          setCurrentTheme(newTheme);
          if (sponsorLogoFullMonoURL) {
            setLogoFullMonoURL(sponsorLogoFullMonoURL);
          }
          if (sponsorLogoSquareMonoURL) {
            setLogoSquareMonoURL(sponsorLogoSquareMonoURL);
          }
          if (sponsor?.name) {
            setSponsorName(sponsor.name);
          }
        }
      }
      setStatus("succeeded");
    } catch (error) {
      const errorString = `Error updating ProgramSponsors: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
  };

  return {
    theme: currentTheme,
    logoFullMonoURL,
    logoSquareMonoURL,
    name: sponsorName,
    status,
    error,
    getSponsorBranding
  };
};
